import {
  TextElementMixin,
} from "%/dynamic-components/render-engine/components/production/elements/text-element/text-element-mixin";
import Label from "%/dynamic-components/render-engine/options/url-options/label";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import BasicTextOptions from "%/dynamic-components/render-engine/bundles/basic-text-options";
import TextOptions from "%/dynamic-components/render-engine/bundles/text-options";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import LayoutWidthHeightOptions from "%/dynamic-components/render-engine/bundles/layout-width-height-options";
import BorderOptions from "%/dynamic-components/render-engine/bundles/border-options";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import HrefOption from "%/dynamic-components/render-engine/options/url-options/href-option";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import BackgroundRepeat from "%/dynamic-components/render-engine/options/background-options/background-repeat";
import BackgroundPosition from "%/dynamic-components/render-engine/options/background-options/background-position";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import BackgroundSize from "%/dynamic-components/render-engine/options/background-options/background-size";
import BorderRadius from "%/dynamic-components/render-engine/options/border-options/border-radius";
import BorderSides from "%/dynamic-components/render-engine/options/border-options/border-sides";
import BrandedTextStyleOptions from "%/dynamic-components/render-engine/bundles/branded-text-style-options";
import { DynamicElementMixin } from "%/dynamic-components/render-engine/mixins/production/dynamic-element-mixin";
import LinkType from "%/dynamic-components/render-engine/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import DynamicComponentError from "%/errors/dynamic-component-error";
import HrefAction from "%/dynamic-components/render-engine/options/click-actions/href-action";
import TextOption from "%/dynamic-components/render-engine/options/text-options/text-option";
import { resolveLinkText } from "%/dynamic-component-editor/helpers/link-text-migration";
import BackgroundGradient from "%/dynamic-components/render-engine/options/background-options/background-gradient";
import EffectsOptions from "%/dynamic-components/render-engine/bundles/effects-options";
import TypographyBranding from "%/dynamic-components/render-engine/classes/branding/typography-branding";

export const UrlElementMixin = {
  // UrlElement carries `BrandedTextStyleOptions` (text-branding-style-
  // option + styled-text-variant) so an author can point a link at a
  // typography variant — e.g. wrap a story title in a UrlElement and
  // ask it to render with the destination's `title` branded styles
  // (different color from `paragraph`). Declaring the branding type
  // is what wires the resolver to walk the typography chain when the
  // element renders; without it, every UrlElement falls back to the
  // iLink's default `color.font.branded` and `text-branding-style-
  // option` becomes a UI option with no visual effect.
  brandingType: TypographyBranding,
  mixins: [DynamicElementMixin, TextElementMixin],
  props: {
    href: {
      type: String,
      required: false,
      default: "", // this is for reusability purposes
    },
  },
  data() {
    return {
      HrefOption,
      Label,
      LayoutWidth,
      LayoutHeight,
      BaseOption,
      BackgroundRepeat,
      BackgroundPosition,
      BackgroundColor,
      BackgroundSize,
    };
  },
  optionsOrder: [
    "link-options",
  ],
  optionBundles: [
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    new BrandedTextStyleOptions({
      "default.text-branding-style-option": "paragraph",
    }, {
      "text-option": true,
    }),
    BasicTextOptions,
    TextOptions,
    SpacingOptions,
    new LayoutWidthHeightOptions({
      "default.layout-width": "hug",
      "default.layout-height": "hug",
    }, {
      "layout-height": true,
      "max-height": true,
      "min-height": true,
    }),
    BorderOptions,
    new BackgroundOptions({}, {
      "background-image": true,
      "background-gradient": true,
    }),
    EffectsOptions,
  ],
  options: [
    new HrefOption(undefined, true),
    new Label(undefined, true),
    new LinkType(undefined, true),
  ],
  computed: {
    editableText() {
      return false;
    },
    hasLabel() {
      return this.label !== undefined && this.label !== null && this.label !== "";
    },
    label() {
      return "";
    },
    target() {
      return this.getOptionValue(HrefAction)?.openInNewTab ? "_blank" : "_self";
    },
    rel() {
      return "";
    },
    myStyleOverrides() {
      return { ...this.textStyleOverrides, ...this.urlStyleOverrides };
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    urlStyleOverrides() {
      const overrides = {
        backgroundColor: this.getOptionValue(BackgroundColor),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
      };
      if (this.shouldShowLink) {
        overrides.borderRadius = this.myBorderRadius;
        overrides.backgroundGradient = this.getOptionValue(BackgroundGradient);
        overrides.backgroundGradientHover = this.getOptionValue(BackgroundGradient, BaseOption.$states.HOVER);
      }
      return overrides;
    },
    computedHref() {
      const linkValues = this.getOptionValue(HrefAction);
      if (!linkValues) {
        return "";
      }
      const urlType = linkValues.linkType;
      let urlValue = linkValues.value;

      if (typeof urlValue === "string" && urlValue.startsWith("http")) {
        return urlValue;
      } else if (typeof urlValue === "object") {
        urlValue = urlValue.value;
      }

      switch (urlType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          if (typeof urlValue === "string" && urlValue.startsWith("/") && !urlValue.startsWith("//")) {
            return `${this.links.web}${urlValue}`;
          }
          return urlValue;
        case LinkType.LINK_TYPE_PAGE:
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          return `${this.links.web}/${urlValue}`;
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            urlType,
            urlValue,
          });
      }
    },
    shouldShowLink() {
      return true;
    },
    changeListMessage() {
      let message = `Element label value updated for ${this.dynamicComponentName}`;
      if (this.isPage) {
        message = `Element label value updated for the ${this.dynamicComponentName}`;
      }
      return message;
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    // Published links authored before the text moved still hold it on
    // `href-action.label`; without this they render blank in production.
    textOptionValue() {
      return resolveLinkText(this.getOptionValue(TextOption), this.getOptionValue(HrefAction));
    },
  },
  methods: {
    // Suppress link navigation when the editor mounts a UrlElement
    // (the surface a custom StoryCard DC uses for "open story" rows).
    // Without this the underlying `<a href>` follows the link the
    // moment the author clicks the card, yanking them out of the
    // builder. `handleClick` (inherited via TextElementMixin → this
    // mixin) already runs the inspect-only path when `editMode` is
    // true, so route the iLink's @click through here and let the
    // browser navigate only in production.
    handleEditorClick(event) {
      if (this.editMode) {
        event.preventDefault();
        this.handleClick(event);
      }
    },
  },
};
